import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Table, Button, Spinner, Alert } from 'react-bootstrap';
import RestoreRecordModal from './RestoreRecordModal';
import { formatColumnValue } from './recordColumnUtils';

const compareValues = (aValue, bValue, sortType) => {
    if (aValue === null || aValue === undefined || aValue === '') return -1;
    if (bValue === null || bValue === undefined || bValue === '') return 1;

    if (sortType === 'date') {
        return new Date(aValue) - new Date(bValue);
    }
    if (sortType === 'number') {
        return Number(aValue) - Number(bValue);
    }
    return aValue.toString().toLowerCase().localeCompare(bValue.toString().toLowerCase());
};

const RecordsTable = ({ recordType, organization }) => {
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [restoreTarget, setRestoreTarget] = useState(null);
    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc');

    const loadRecords = useCallback(async () => {
        setLoading(true);
        setError('');

        try {
            const response = await recordType.fetchRecords(organization.id);
            if (response.success) {
                setRecords(Array.isArray(response.data) ? response.data : []);
            } else {
                setError(response.error || `Failed to load ${recordType.label.toLowerCase()}`);
                setRecords([]);
            }
        } catch (err) {
            console.error(`Error loading ${recordType.label}:`, err);
            setError(`Failed to load ${recordType.label.toLowerCase()}`);
            setRecords([]);
        } finally {
            setLoading(false);
        }
    }, [recordType, organization.id]);

    useEffect(() => {
        setSortField(null);
        setSortDirection('asc');
        loadRecords();
    }, [loadRecords]);

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const getSortIcon = (field) => {
        if (sortField !== field) {
            return <i className="bi bi-arrow-down-up ms-1 text-muted"></i>;
        }
        return sortDirection === 'asc' ?
            <i className="bi bi-arrow-up ms-1 text-primary"></i> :
            <i className="bi bi-arrow-down ms-1 text-primary"></i>;
    };

    const sortedRecords = useMemo(() => {
        if (!sortField) return records;
        const column = recordType.columns.find(col => col.key === sortField);
        const sortType = column?.sortType || 'string';

        return [...records].sort((a, b) => {
            const result = compareValues(a[sortField], b[sortField], sortType);
            return sortDirection === 'asc' ? result : -result;
        });
    }, [records, sortField, sortDirection, recordType.columns]);

    let content;
    if (loading) {
        content = (
            <div className="text-center py-4">
                <Spinner animation="border" size="sm" className="me-2" />
                Loading {recordType.label.toLowerCase()}...
            </div>
        );
    } else if (error) {
        content = (
            <Alert variant="danger" className="mb-0">
                <i className="bi bi-exclamation-triangle me-2"></i>{error}
            </Alert>
        );
    } else if (records.length === 0) {
        content = (
            <div className="text-center py-4 text-muted">
                <i className={`bi ${recordType.icon} display-4`}></i>
                <div className="mt-2">No archived {recordType.label.toLowerCase()} found</div>
            </div>
        );
    } else {
        content = (
            <Table responsive hover className="mb-0">
                <thead>
                    <tr>
                        {recordType.columns.map(col => (
                            <th
                                key={col.key}
                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                onClick={() => handleSort(col.key)}
                            >
                                {col.label} {getSortIcon(col.key)}
                            </th>
                        ))}
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {sortedRecords.map(record => (
                        <tr key={record.id}>
                            {recordType.columns.map(col => (
                                <td key={col.key}>{formatColumnValue(record[col.key], col)}</td>
                            ))}
                            <td>
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => setRestoreTarget(record)}
                                    title="Restore"
                                >
                                    <i className="bi bi-arrow-counterclockwise me-1"></i>Restore
                                </Button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        );
    }

    // The modal must stay mounted across loadRecords()-driven re-renders
    // (e.g. right after a successful restore) - otherwise its "submitted"
    // state and auto-close timer are destroyed and it reopens on remount.
    return (
        <>
            {content}

            <RestoreRecordModal
                show={!!restoreTarget}
                onHide={() => setRestoreTarget(null)}
                record={restoreTarget}
                columns={recordType.columns}
                recordLabel={recordType.singularLabel || recordType.label}
                organization={organization}
                restoreRecord={recordType.restoreRecord}
                onRestored={loadRecords}
            />
        </>
    );
};

export default RecordsTable;
